import {
  type SavableProperty,
  useStoreSavableProperty,
} from "#src/composables/savable-property.composable.ts";

import {
  validateDate,
  validateInList,
} from "#src/composables/savable-property-validators.ts";
import {
  SMOKING_FREQUENCIES,
  SMOKING_TYPES,
  type SmokingCategory,
} from "#src/constants/smoking-usage.ts";
import { generateUuid } from "#src/util/helpers.ts";
import {
  defineStore,
  generateInitialState,
  resetState,
  type ToSetupState,
  type SetupStore,
} from "#src/stores/state-wrapper.ts";
import { type Pinia, type StoreGeneric } from "pinia";

export const smokingUsageNameGenerator = () =>
  `smoking-usage-${generateUuid()}`;

interface SmokingUsageState {
  id: string | null;
  last_use_date: SavableProperty<string>;
  category: SavableProperty<SmokingCategory>;
  frequency: SavableProperty<number>;
}
export interface SmokingUsageStore
  extends SetupStore,
    ToSetupState<SmokingUsageState> {}

function initialState(): SmokingUsageState {
  return {
    id: null,
    last_use_date: useStoreSavableProperty<string>({
      requestMap: "last_use_date",
      group: "smoker",
      rules: {
        dateIsBeforeNow: validateDate({
          isSameOrBefore: true,
          otherDateProperty: () => new Date(),
        }),
      },
    }),
    category: useStoreSavableProperty<SmokingCategory>({
      requestMap: "category",
      group: "smoker",
      rules: {
        inList: validateInList(SMOKING_TYPES),
      },
    }),
    frequency: useStoreSavableProperty<number>({
      requestMap: "frequency",
      group: "smoker",
      rules: {
        inList: validateInList(SMOKING_FREQUENCIES.map(f => f.value)),
      },
    }),
  };
}

function initializeSmokingUsage(): SmokingUsageStore {
  const state = generateInitialState<SmokingUsageState>(initialState());

  function $reset() {
    resetState<SmokingUsageState>(state, initialState());
  }

  return { $reset, ...state };
}

export const useSmokingUsage = (
  id: string,
  pinia: Pinia,
  hot?: StoreGeneric
) => {
  return defineStore<string, SmokingUsageStore>(id, () => {
    return initializeSmokingUsage();
  })(pinia, hot);
};
